import {
  ContentService,
  type FlashcardSavedPublic,
  FlashcardsService,
  type QuizQuestionSavedListItem,
  QuizzesService,
} from "@/client"
import { AppEmptyState } from "@/components/Common/AppEmptyState"
import { HtmlContent } from "@/components/Common/HtmlContent"
import { FlashcardContent } from "@/components/Flashcards/FlashcardContent"
import { PageShell, PageSkeleton } from "@/components/Layout"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"
import { getReviewQuestionAnchor } from "@/utils/review-question-anchor"
import { stripOptionPrefix } from "@/utils/text-format"
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Stack,
  Tabs,
  Text,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import { FaRegBookmark } from "react-icons/fa"

export const Route = createFileRoute("/_layout/saved-questions")({
  component: SavedQuestionsPage,
})

export function SavedQuestionsPage() {
  const breadcrumbs = useRouteBreadcrumbs("dashboard.root", [
    { label: "Bookmarks", isCurrent: true },
  ])
  const [activeTab, setActiveTab] = useState("questions")
  const [subjectFilter, setSubjectFilter] = useState<string | null>(null)
  const { data, isLoading } = useQuery({
    queryKey: ["quiz-question-saved-list"],
    queryFn: listAllSavedQuizQuestions,
  })
  const { data: flashcardData, isLoading: isFlashcardsLoading } = useQuery({
    queryKey: ["flashcards-saved"],
    queryFn: () => FlashcardsService.listSavedFlashcards(),
    enabled: activeTab === "flashcards",
  })
  const { data: subjectsData } = useQuery({
    queryKey: ["subjects"],
    queryFn: () => ContentService.listSubjects({ limit: 100 }),
    enabled: activeTab === "flashcards",
  })
  const savedQuestions = data?.data ?? []
  const savedFlashcards = flashcardData?.data ?? []
  const subjectNamesById = new Map(
    (subjectsData?.data ?? []).map((subject) => [subject.id, subject.name]),
  )
  const questionSubjects = useMemo(
    () =>
      Array.from(
        new Set(savedQuestions.flatMap((item) => item.subject_names ?? [])),
      ).sort(),
    [savedQuestions],
  )
  const flashcardSubjects = useMemo(
    () =>
      Array.from(
        new Set(
          savedFlashcards
            .map((card) =>
              card.subject_id ? subjectNamesById.get(card.subject_id) : null,
            )
            .filter((name): name is string => Boolean(name)),
        ),
      ).sort(),
    [savedFlashcards, subjectNamesById],
  )

  if (isLoading) return <PageSkeleton variant="table" rows={4} />

  const grouped = savedQuestions.reduce<
    Record<string, { quizName: string; items: QuizQuestionSavedListItem[] }>
  >((groups, item) => {
    const quizGroup = groups[item.quiz_id] ?? {
      quizName: item.quiz_name,
      items: [],
    }
    quizGroup.items.push(item)
    groups[item.quiz_id] = quizGroup
    return groups
  }, {})

  const subjects =
    activeTab === "questions" ? questionSubjects : flashcardSubjects
  const filteredQuestions = subjectFilter
    ? savedQuestions.filter((item) =>
        item.subject_names?.includes(subjectFilter),
      )
    : savedQuestions
  const filteredFlashcards = subjectFilter
    ? savedFlashcards.filter(
        (card) => subjectNamesById.get(card.subject_id ?? "") === subjectFilter,
      )
    : savedFlashcards

  return (
    <PageShell
      title="Bookmarks"
      description="Review saved flashcards and saved questions from quizzes."
      breadcrumbs={breadcrumbs}
    >
      <Tabs.Root
        value={activeTab}
        onValueChange={({ value }) => {
          setActiveTab(value)
          setSubjectFilter(null)
        }}
      >
        <Tabs.List>
          <Tabs.Trigger value="questions">Saved Questions</Tabs.Trigger>
          <Tabs.Trigger value="flashcards">Saved Flashcards</Tabs.Trigger>
        </Tabs.List>
        <SubjectFilters
          subjects={subjects}
          value={subjectFilter}
          onChange={setSubjectFilter}
        />
        <Tabs.Content value="questions">
          {filteredQuestions.length === 0 ? (
            <AppEmptyState
              title="No saved questions found"
              description="Bookmark a question or add a note while reviewing a completed quiz."
              icon={<FaRegBookmark />}
            />
          ) : (
            <Stack gap={6}>
              {Object.entries(grouped).map(([quizId, group]) => {
                const items = group.items.filter((item) =>
                  filteredQuestions.some((filtered) => filtered.id === item.id),
                )
                if (!items.length) return null
                return (
                  <Stack key={quizId} gap={3}>
                    {items.map((item) => (
                      <SavedQuestionRow
                        key={item.id}
                        item={item}
                        quizName={group.quizName}
                      />
                    ))}
                  </Stack>
                )
              })}
            </Stack>
          )}
        </Tabs.Content>
        <Tabs.Content value="flashcards">
          {isFlashcardsLoading ? (
            <PageSkeleton variant="table" rows={3} />
          ) : filteredFlashcards.length === 0 ? (
            <AppEmptyState
              title="No saved flashcards found"
              description="Bookmark a flashcard or add a note during study."
              icon={<FaRegBookmark />}
            />
          ) : (
            <Stack gap={3}>
              {filteredFlashcards.map((card) => (
                <SavedFlashcardRow
                  key={card.id}
                  card={card}
                  subjectName={
                    card.subject_id
                      ? subjectNamesById.get(card.subject_id)
                      : undefined
                  }
                />
              ))}
            </Stack>
          )}
        </Tabs.Content>
      </Tabs.Root>
    </PageShell>
  )
}

function SubjectFilters({
  subjects,
  value,
  onChange,
}: {
  subjects: string[]
  value: string | null
  onChange: (value: string | null) => void
}) {
  if (!subjects.length) return null
  return (
    <Flex gap={2} wrap="wrap" mt={4} aria-label="Subject filters">
      <Button
        size="xs"
        variant={value === null ? "solid" : "outline"}
        onClick={() => onChange(null)}
      >
        All subjects
      </Button>
      {subjects.map((subject) => (
        <Button
          key={subject}
          size="xs"
          variant={value === subject ? "solid" : "outline"}
          onClick={() => onChange(subject)}
        >
          {subject}
        </Button>
      ))}
    </Flex>
  )
}

function SavedFlashcardRow({
  card,
  subjectName,
}: { card: FlashcardSavedPublic; subjectName?: string }) {
  return (
    <Box borderWidth={1} borderRadius="lg" bg="bg.surface" p={4}>
      <Stack gap={3}>
        {subjectName && (
          <Badge colorPalette="teal" variant="subtle" alignSelf="flex-start">
            {subjectName}
          </Badge>
        )}
        <Text fontSize="sm" fontWeight="semibold" color="fg.muted">
          Deck: {card.deck_name}
        </Text>
        <Heading size="sm">
          <FlashcardContent content={card.front} />
        </Heading>
        {card.note_html && (
          <Box
            bg="purple.subtle"
            borderColor="purple.300"
            borderWidth="1px"
            borderRadius="md"
            p={3}
          >
            <Text fontSize="xs" fontWeight="semibold" color="purple.fg" mb={1}>
              Your note
            </Text>
            <HtmlContent content={card.note_html} color="fg.muted" />
          </Box>
        )}
        <Button size="sm" alignSelf="flex-end" asChild>
          <Link to="/study/saved/$cardId" params={{ cardId: card.card_id }}>
            Open card
          </Link>
        </Button>
      </Stack>
    </Box>
  )
}

async function listAllSavedQuizQuestions() {
  const limit = 200
  let offset = 0
  const items: QuizQuestionSavedListItem[] = []

  while (true) {
    const page = await QuizzesService.listSavedQuizQuestions({ offset, limit })
    items.push(...page.data)
    if (items.length >= page.count || page.data.length === 0) {
      return { data: items, count: page.count }
    }
    offset += page.data.length
  }
}

function SavedQuestionRow({
  item,
  quizName,
}: { item: QuizQuestionSavedListItem; quizName: string }) {
  return (
    <Box borderWidth="1px" borderRadius="lg" bg="bg.surface" p={4}>
      <Stack gap={3}>
        <Text fontSize="sm" fontWeight="semibold" color="fg.muted">
          Quiz: {quizName}
        </Text>
        {item.subject_names?.length ? (
          <Flex gap={2} wrap="wrap">
            {item.subject_names.map((subject) => (
              <Badge key={subject} colorPalette="teal" variant="subtle">
                {subject}
              </Badge>
            ))}
          </Flex>
        ) : null}
        <Heading size="sm" lineHeight="1.35">
          <HtmlContent content={item.question_text} />
        </Heading>
        <Stack gap={2}>
          {item.options.map((option, optionIndex) => {
            const isCorrect = optionIndex === item.correct_answer
            return (
              <Flex
                key={optionIndex}
                align="center"
                justify="space-between"
                gap={3}
                borderWidth="1px"
                borderColor={isCorrect ? "green.500" : "border"}
                borderRadius="md"
                bg={isCorrect ? "green.subtle" : "bg.subtle"}
                p={3}
              >
                <Text>
                  {String.fromCharCode(65 + optionIndex)}.{" "}
                  {stripOptionPrefix(option)}
                </Text>
                {isCorrect && (
                  <Badge colorPalette="green" flexShrink={0}>
                    Correct answer
                  </Badge>
                )}
              </Flex>
            )
          })}
        </Stack>
        {item.rationale && (
          <Box
            bg="blue.subtle"
            borderColor="blue.300"
            borderWidth="1px"
            borderRadius="md"
            p={3}
          >
            <Text fontSize="xs" fontWeight="semibold" color="blue.fg" mb={1}>
              Rationale
            </Text>
            <HtmlContent content={item.rationale} color="fg.muted" />
          </Box>
        )}
        {item.note_html && (
          <Box
            bg="purple.subtle"
            borderColor="purple.300"
            borderWidth="1px"
            borderRadius="md"
            p={3}
          >
            <Text fontSize="xs" fontWeight="semibold" color="purple.fg" mb={1}>
              Your note
            </Text>
            <HtmlContent content={item.note_html} />
          </Box>
        )}
        {item.latest_review_attempt_id ? (
          <Button size="sm" alignSelf="flex-end" asChild>
            <Link
              to="/quizzes/$quizId/attempts/$attemptId/review"
              params={{
                quizId: item.quiz_id,
                attemptId: item.latest_review_attempt_id,
              }}
              hash={getReviewQuestionAnchor(item.question_id)}
            >
              Open review
            </Link>
          </Button>
        ) : (
          <Text fontSize="sm" color="fg.muted">
            No completed attempt is available for review.
          </Text>
        )}
      </Stack>
    </Box>
  )
}

export default SavedQuestionsPage
